import { useCallback, useRef, useState } from 'react';
import type { ComponentProps, CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import {
  Toast,
  ToastActionButton,
  ToastContent,
  ToastDescription,
  ToastIndicator,
  ToastTitle,
  toast as heroToast,
} from '@heroui/react';
import type { ToastContentValue } from '@heroui/react';

type ToastProviderChildren = ComponentProps<typeof Toast.Provider>['children'];
type ToastProviderRender = Extract<ToastProviderChildren, (props: never) => unknown>;
type ToastItem = Parameters<ToastProviderRender>[0]['toast'];

interface SwipeDismissToastProps {
  toast: ToastItem;
}

const MAX_SWIPE_DISTANCE = 160;
const DISMISS_DISTANCE = 80;
const DOMINANCE_THRESHOLD = 8;

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('button,input,select,textarea,a,[role="button"]'));
}

function clampSwipeDistance(deltaY: number): number {
  return Math.max(-MAX_SWIPE_DISTANCE, Math.min(0, deltaY));
}

export function SwipeDismissToast({ toast }: SwipeDismissToastProps) {
  const startPointRef = useRef<{ x: number; y: number } | null>(null);
  const gestureCancelledRef = useRef(false);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const content = toast.content as ToastContentValue;

  const resetGesture = useCallback(() => {
    startPointRef.current = null;
    gestureCancelledRef.current = false;
    setIsDragging(false);
    setOffsetY(0);
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent) => {
    if (event.button !== 0 || isInteractiveTarget(event.target)) return;

    startPointRef.current = { x: event.clientX, y: event.clientY };
    gestureCancelledRef.current = false;
    setIsDragging(false);
  }, []);

  const onPointerMove = useCallback((event: ReactPointerEvent) => {
    const startPoint = startPointRef.current;
    if (!startPoint || gestureCancelledRef.current) return;

    const deltaX = event.clientX - startPoint.x;
    const deltaY = event.clientY - startPoint.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (!isDragging) {
      if (absX > absY && absX > DOMINANCE_THRESHOLD) {
        gestureCancelledRef.current = true;
        setOffsetY(0);
        return;
      }

      if (deltaY < 0 && absY > absX && absY > DOMINANCE_THRESHOLD) {
        setIsDragging(true);
      } else {
        return;
      }
    }

    if (deltaY < 0) {
      event.preventDefault();
      setOffsetY(clampSwipeDistance(deltaY));
    }
  }, [isDragging]);

  const onPointerUp = useCallback(() => {
    const shouldDismiss = offsetY <= -DISMISS_DISTANCE;
    startPointRef.current = null;
    gestureCancelledRef.current = false;
    setIsDragging(false);

    if (shouldDismiss) {
      heroToast.close(toast.key);
      return;
    }

    setOffsetY(0);
  }, [offsetY, toast.key]);

  const onPointerCancel = useCallback(() => {
    resetGesture();
  }, [resetGesture]);

  const onPointerLeave = useCallback(() => {
    if (startPointRef.current) resetGesture();
  }, [resetGesture]);

  const style = {
    transform: `translateY(${offsetY}px)`,
    transition: isDragging ? 'none' : 'transform 180ms ease-out',
    touchAction: 'none',
  } satisfies CSSProperties;

  return (
    <Toast
      toast={toast}
      variant={content.variant}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={onPointerLeave}
    >
      <ToastContent>
        <div className="flex items-center gap-2.5 pe-8">
          <ToastIndicator variant={content.variant}>{content.indicator}</ToastIndicator>
          <div className="flex flex-col gap-0.5">
            {content.title ? <ToastTitle>{content.title}</ToastTitle> : null}
            {content.description ? <ToastDescription>{content.description}</ToastDescription> : null}
          </div>
        </div>
      </ToastContent>
      {content.actionProps ? <ToastActionButton {...content.actionProps} /> : null}
      <Toast.CloseButton className="absolute end-2 top-1/2 -translate-y-1/2 border-none bg-transparent opacity-100 [&>svg]:size-4" />
    </Toast>
  );
}
